import React from "react";

const UserDetailModal = ({ user, isOpen, onClose }) => {
  if (!isOpen || !user) return null;

  // Format date
  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get user initials
  const getUserInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  return (
    <div className="user-detail-modal-overlay" onClick={onClose}>
      <div className="user-detail-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="user-detail-modal-header">
          <h4 className="text-20 lh-1 fw-500">User Details</h4>
          <button className="user-detail-modal-close" onClick={onClose}>
            <i className="icon-close"></i>
          </button>
        </div>

        <div className="user-detail-modal-body">
          {/* User Avatar and Basic Info */}
          <div className="user-detail-header mb-30">
            <div className="d-flex items-center">
              <div className="size-80 d-flex justify-center items-center bg-purple-3 rounded-full mr-20">
                <div className="text-24 fw-500 text-purple-1">
                  {getUserInitials(user.firstName, user.lastName)}
                </div>
              </div>
              <div>
                <h5 className="text-18 lh-1 fw-500 mb-5">{user.fullName}</h5>
                <p className="text-15 text-light-1 mb-5">{user.email}</p>
                <span className={`badge px-15 py-5 rounded-4 ${
                  user.isActive ? 'bg-green-1 text-white' : 'bg-orange-1 text-white'
                }`}>
                  {user.isActive ? 'Active' : 'Pending Activation'}
                </span>
              </div>
            </div>
          </div>

          {/* User Information Grid */}
          <div className="row y-gap-20">
            <div className="col-md-6">
              <div className="user-detail-item">
                <label className="text-13 text-light-1 uppercase fw-500 mb-5">Role</label>
                <p className="text-15 fw-500 capitalize">{user.role}</p>
              </div>
            </div>

            <div className="col-md-6">
              <div className="user-detail-item">
                <label className="text-13 text-light-1 uppercase fw-500 mb-5">Mobile Number</label>
                <p className="text-15">{user.mobileNumber || 'Not provided'}</p>
              </div>
            </div>

            <div className="col-md-6">
              <div className="user-detail-item">
                <label className="text-13 text-light-1 uppercase fw-500 mb-5">Address</label>
                <p className="text-15">{user.address || 'Not provided'}</p>
              </div>
            </div>

            <div className="col-md-6">
              <div className="user-detail-item">
                <label className="text-13 text-light-1 uppercase fw-500 mb-5">State</label>
                <p className="text-15">{user.state || 'Not provided'}</p>
              </div>
            </div>

            <div className="col-md-6">
              <div className="user-detail-item">
                <label className="text-13 text-light-1 uppercase fw-500 mb-5">Country</label>
                <p className="text-15">{user.country || 'Not provided'}</p>
              </div>
            </div>

            <div className="col-md-6">
              <div className="user-detail-item">
                <label className="text-13 text-light-1 uppercase fw-500 mb-5">Email Verified</label>
                <p className="text-15">
                  {user.isEmailVerified ? (
                    <span className="text-green-1">✓ Verified</span>
                  ) : (
                    <span className="text-orange-1">✗ Not Verified</span>
                  )}
                </p>
              </div>
            </div>

            <div className="col-md-6">
              <div className="user-detail-item">
                <label className="text-13 text-light-1 uppercase fw-500 mb-5">Invitation Type</label>
                <p className="text-15 capitalize">{user.invitationType?.replace('-', ' ') || 'Direct Signup'}</p>
              </div>
            </div>

            <div className="col-md-6">
              <div className="user-detail-item">
                <label className="text-13 text-light-1 uppercase fw-500 mb-5">Must Change Password</label>
                <p className="text-15">
                  {user.mustChangePassword ? (
                    <span className="text-orange-1">Yes</span>
                  ) : (
                    <span className="text-green-1">No</span>
                  )}
                </p>
              </div>
            </div>

            <div className="col-md-6">
              <div className="user-detail-item">
                <label className="text-13 text-light-1 uppercase fw-500 mb-5">Last Login</label>
                <p className="text-15">{formatDate(user.lastLogin)}</p>
              </div>
            </div>

            <div className="col-md-6">
              <div className="user-detail-item">
                <label className="text-13 text-light-1 uppercase fw-500 mb-5">Joined Date</label>
                <p className="text-15">{formatDate(user.createdAt)}</p>
              </div>
            </div>

            <div className="col-md-6">
              <div className="user-detail-item">
                <label className="text-13 text-light-1 uppercase fw-500 mb-5">Last Updated</label>
                <p className="text-15">{formatDate(user.updatedAt)}</p>
              </div>
            </div>

            <div className="col-md-6">
              <div className="user-detail-item">
                <label className="text-13 text-light-1 uppercase fw-500 mb-5">Email Verified At</label>
                <p className="text-15">{formatDate(user.emailVerifiedAt)}</p>
              </div>
            </div>

            {user.personalInfo && (
              <div className="col-12">
                <div className="user-detail-item">
                  <label className="text-13 text-light-1 uppercase fw-500 mb-5">Personal Information</label>
                  <p className="text-15">{user.personalInfo}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="user-detail-modal-footer">
          <button className="button bg-light-4 text-light-1 fw-500" onClick={onClose}>
            Close
          </button>
        </div>
      </div>

      {/* Inline Styles for Modal */}
      <style dangerouslySetInnerHTML={{
        __html: `
          .user-detail-modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0, 0, 0, 0.5);
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 1000;
          }

          .user-detail-modal-content {
            background: white;
            border-radius: 16px;
            width: 90%;
            max-width: 800px;
            max-height: 90vh;
            overflow-y: auto;
            box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
          }

          .user-detail-modal-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 30px 30px 0 30px;
            border-bottom: 1px solid #e9ecef;
            margin-bottom: 30px;
            padding-bottom: 20px;
          }

          .user-detail-modal-close {
            background: none;
            border: none;
            font-size: 18px;
            cursor: pointer;
            color: #6c757d;
            padding: 5px;
          }

          .user-detail-modal-close:hover {
            color: #495057;
          }

          .user-detail-modal-body {
            padding: 0 30px;
          }

          .user-detail-modal-footer {
            padding: 20px 30px 30px 30px;
            display: flex;
            justify-content: flex-end;
          }

          .user-detail-item {
            padding: 15px;
            background: #f8f9fa;
            border-radius: 8px;
          }

          .user-detail-header {
            padding: 20px;
            background: #f8f9fa;
            border-radius: 12px;
          }
        `
      }} />
    </div>
  );
};

export default UserDetailModal; 